/**
 * SDR Stock Manager - Bidirectional Incremental Synchronization Engine & Retry Outbox
 * @module services/sync
 */

import { TABLES, SYNC_CONFIG } from '../config/constants.js';
import {
  getState,
  setState,
  getLastCloudSyncAt,
  setLastCloudSyncAt,
  getCloudSyncInProgress,
  setCloudSyncInProgress,
  getCloudSyncQueued,
  setCloudSyncQueued,
  setRealtimeSelfPauseUntil
} from '../state/store.js';
import { saveLocalData } from '../state/storage.js';
import {
  hasCloudSyncConfig,
  fetchSupabaseTableRows,
  supabaseRequest,
  upsertSupabaseTable,
  fromInventoryRow,
  toInventoryRow,
  fromTransactionRow,
  toTransactionRow,
  fromStockAuditRow,
  toStockAuditRow,
  fromStockOutMasterRow,
  toStockOutMasterRow,
  fromStockOutDetailsRow,
  toStockOutDetailsRow
} from './supabase.js';

let pendingSyncRetryTimer = null;
let pendingSyncRetryAttempt = 0;
let pendingSyncRetryPushSettings = false;
let backgroundSyncInProgress = false;

/**
 * Merges local and server record collections using Last-Write-Wins (LWW) conflict resolution based on ISO timestamps.
 * @template T
 * @param {T[]} localRows
 * @param {T[]} serverRows
 * @param {(row: T) => string} getUpdatedAt
 * @returns {T[]}
 */
export function mergeByLatest(localRows = [], serverRows = [], getUpdatedAt = r => r?.updatedAt) {
  const merged = new Map();

  for (const row of serverRows || []) {
    if (row && row.id) {
      merged.set(row.id, row);
    }
  }

  for (const row of localRows || []) {
    if (!row || !row.id) continue;
    const existing = merged.get(row.id);
    if (!existing) {
      merged.set(row.id, row);
      continue;
    }

    const localTime = new Date(getUpdatedAt(row) || 0).getTime();
    const serverTime = new Date(getUpdatedAt(existing) || 0).getTime();

    // Local change wins if newer or equal (Last-Write-Wins)
    if (localTime >= serverTime) {
      merged.set(row.id, row);
    }
  }

  return Array.from(merged.values());
}

/**
 * Identifies local records that are newer than their server counterparts or missing on server.
 * @template T
 * @param {T[]} localRows
 * @param {T[]} serverRows
 * @param {(row: T) => string} getUpdatedAt
 * @returns {T[]}
 */
export function getRowsNewerThanServer(localRows = [], serverRows = [], getUpdatedAt = r => r?.updatedAt, toleranceMs = 1000) {
  const serverMap = new Map();
  for (const row of serverRows || []) {
    if (row && row.id) {
      serverMap.set(row.id, row);
    }
  }

  const tol = typeof toleranceMs === 'number' ? toleranceMs : 1000;

  return (localRows || []).filter(row => {
    if (!row || !row.id) return false;
    const serverRow = serverMap.get(row.id);
    if (!serverRow) return true; // Missing on server -> needs push

    const localTime = Date.parse(getUpdatedAt(row) || '');
    const serverTime = Date.parse(getUpdatedAt(serverRow) || '');
    if (!Number.isFinite(localTime)) return false;
    if (!Number.isFinite(serverTime)) return true;
    return (localTime - serverTime) > tol;
  });
}

/**
 * Filters rows updated strictly after a given ISO timestamp.
 * @template T
 * @param {T[]} rows
 * @param {string} timestamp
 * @param {(row: T) => string} getUpdatedAt
 * @returns {T[]}
 */
export function getRowsNewerThanTimestamp(rows = [], timestamp = '', getUpdatedAt = r => r?.updatedAt) {
  const sinceTime = Date.parse(timestamp || '');
  if (!Number.isFinite(sinceTime)) {
    return (rows || []).filter(row => row && row.id);
  }

  return (rows || []).filter(row => {
    if (!row || !row.id) return false;
    const rowTime = Date.parse(getUpdatedAt(row) || '');
    return Number.isFinite(rowTime) && rowTime > sinceTime;
  });
}

/**
 * Builds PostgREST filter query array for incremental `updated_at=gt.<timestamp>`.
 * @param {string} timestamp
 * @returns {string[]}
 */
export function getIncrementalUpdatedAtFilter(timestamp) {
  const syncTime = Date.parse(timestamp || '');
  if (!Number.isFinite(syncTime)) return [];
  return [`updated_at=gt.${encodeURIComponent(new Date(syncTime).toISOString())}`];
}

/**
 * Resolves predefined storage locations with resilience between local modifications and server settings.
 * @param {string} serverValue
 * @param {boolean} [forcePull=false]
 * @param {boolean} [serverValueExists=false]
 * @param {string} [localValue='']
 * @returns {string}
 */
export function getResilientStorageLocations(serverValue, forcePull = false, serverValueExists = false, localValue = '') {
  const serverLocations = String(serverValue || '').trim();
  const stateLocations = String(localValue || getState().settings?.storageLocations || '').trim();

  if (serverValueExists) return serverLocations;
  if (serverLocations) return serverLocations;
  if (!forcePull && stateLocations) return stateLocations;
  return stateLocations || '';
}

/**
 * Executes a full or incremental bidirectional synchronization cycle with Supabase PostgREST.
 * @param {Object} [options={}]
 * @param {boolean} [options.forcePull=false] - Force overwrite local state with full cloud dataset
 * @param {boolean} [options.pushSettings=false] - Explicitly push application settings to cloud
 * @param {boolean} [options.silent=false] - Suppress user alerts
 * @param {boolean} [options.throwOnError=false] - Throw error on failure instead of returning object
 * @returns {Promise<import('../types/index.js').SyncResult>}
 */
export async function syncWithSupabase(options = {}) {
  const { forcePull = false, pushSettings = false, throwOnError = false } = options;

  if (!hasCloudSyncConfig()) {
    const error = new Error('Missing Supabase cloud sync configuration');
    if (throwOnError) throw error;
    return { success: false, pushed: 0, pulled: 0, error };
  }

  if (getCloudSyncInProgress()) {
    setCloudSyncQueued(true);
    const error = new Error('Sync already in progress');
    if (throwOnError) throw error;
    return { success: false, pushed: 0, pulled: 0, error };
  }

  setCloudSyncInProgress(true);
  let totalPushed = 0;
  let totalPulled = 0;

  try {
    const syncStartedAt = new Date().toISOString();
    const lastSyncAt = getLastCloudSyncAt();
    const state = getState();
    const hasLocal = (state.inventory && state.inventory.length > 0) || (state.transactions && state.transactions.length > 0);
    const shouldForcePull = forcePull || !hasLocal;
    const useIncrementalSync = !shouldForcePull && Boolean(lastSyncAt);
    const filterOpt = useIncrementalSync
      ? { pageSize: 300, orderBy: 'updated_at', filters: getIncrementalUpdatedAtFilter(lastSyncAt) }
      : { pageSize: 300 };

    // 1. Pull delta/full rows from all 6 Supabase tables in parallel using Promise.allSettled
    const results = await Promise.allSettled([
      fetchSupabaseTableRows(TABLES.INVENTORY, filterOpt),
      fetchSupabaseTableRows(TABLES.TRANSACTIONS, filterOpt),
      fetchSupabaseTableRows(TABLES.APP_SETTINGS, { orderBy: 'key' }),
      fetchSupabaseTableRows(TABLES.STOCK_AUDITS, filterOpt),
      fetchSupabaseTableRows(TABLES.STOCK_OUT_MASTER, filterOpt),
      fetchSupabaseTableRows(TABLES.STOCK_OUT_DETAILS, filterOpt)
    ]);

    if (results[0].status === 'rejected') {
      throw results[0].reason || new Error('Failed to fetch inventory table');
    }
    if (results[1].status === 'rejected') {
      throw results[1].reason || new Error('Failed to fetch transactions table');
    }

    const serverInventoryRows = results[0].status === 'fulfilled' && Array.isArray(results[0].value) ? results[0].value : [];
    const serverTransactionRows = results[1].status === 'fulfilled' && Array.isArray(results[1].value) ? results[1].value : [];
    const serverSettingRows = results[2].status === 'fulfilled' && Array.isArray(results[2].value) ? results[2].value : [];
    const serverStockAuditRows = results[3].status === 'fulfilled' && Array.isArray(results[3].value) ? results[3].value : [];
    const serverStockOutMasterRows = results[4].status === 'fulfilled' && Array.isArray(results[4].value) ? results[4].value : [];
    const serverStockOutDetailsRows = results[5].status === 'fulfilled' && Array.isArray(results[5].value) ? results[5].value : [];

    const serverInventory = (serverInventoryRows || []).map(fromInventoryRow);
    const serverTransactions = (serverTransactionRows || []).map(fromTransactionRow);
    const serverStockAudits = (serverStockAuditRows || []).map(fromStockAuditRow);
    const serverStockOutMaster = (serverStockOutMasterRows || []).map(fromStockOutMasterRow);
    const serverStockOutDetails = (serverStockOutDetailsRows || []).map(fromStockOutDetailsRow);

    const serverSettings = {};
    (serverSettingRows || []).forEach(row => {
      if (row.key) serverSettings[row.key] = row.value;
    });

    totalPulled =
      serverInventory.length +
      serverTransactions.length +
      serverStockAudits.length +
      serverStockOutMaster.length +
      serverStockOutDetails.length;

    // 2. Conflict Resolution (Last-Write-Wins Merge)
    let mergedInventory = state.inventory;
    if (serverInventory.length > 0) {
      mergedInventory = forcePull
        ? serverInventory
        : mergeByLatest(state.inventory, serverInventory, i => i.updatedAt);
    } else if (forcePull && (!state.inventory || state.inventory.length === 0)) {
      mergedInventory = [];
    }

    const mergedTransactions = forcePull
      ? serverTransactions
      : mergeByLatest(state.transactions, serverTransactions, t => t.updatedAt || t.timestamp);

    const mergedStockAudits = forcePull
      ? serverStockAudits
      : mergeByLatest(state.stockAudits, serverStockAudits, a => a.updatedAt);

    const mergedStockOutMaster = forcePull
      ? serverStockOutMaster
      : mergeByLatest(state.stockOutMaster || [], serverStockOutMaster, m => m.updatedAt);

    const mergedStockOutDetails = forcePull
      ? serverStockOutDetails
      : mergeByLatest(state.stockOutDetails || [], serverStockOutDetails, d => d.updatedAt);

    // 3. Outbox Delta Push
    if (!forcePull) {
      const localInvCandidates = useIncrementalSync
        ? getRowsNewerThanTimestamp(state.inventory, lastSyncAt, i => i.updatedAt)
        : state.inventory;
      const localTxCandidates = useIncrementalSync
        ? getRowsNewerThanTimestamp(state.transactions, lastSyncAt, t => t.updatedAt || t.timestamp)
        : state.transactions;
      const localAuditCandidates = useIncrementalSync
        ? getRowsNewerThanTimestamp(state.stockAudits || [], lastSyncAt, a => a.updatedAt)
        : state.stockAudits || [];
      const localSoMCandidates = useIncrementalSync
        ? getRowsNewerThanTimestamp(state.stockOutMaster || [], lastSyncAt, m => m.updatedAt)
        : state.stockOutMaster || [];
      const localSoDCandidates = useIncrementalSync
        ? getRowsNewerThanTimestamp(state.stockOutDetails || [], lastSyncAt, d => d.updatedAt)
        : state.stockOutDetails || [];

      const invToPush = getRowsNewerThanServer(localInvCandidates, serverInventory, i => i.updatedAt).map(toInventoryRow);
      const txToPush = getRowsNewerThanServer(localTxCandidates, serverTransactions, t => t.updatedAt || t.timestamp).map(toTransactionRow);
      const auditsToPush = getRowsNewerThanServer(localAuditCandidates, serverStockAudits, a => a.updatedAt).map(toStockAuditRow);
      const somToPush = getRowsNewerThanServer(localSoMCandidates, serverStockOutMaster, m => m.updatedAt).map(toStockOutMasterRow);
      const sodToPush = getRowsNewerThanServer(localSoDCandidates, serverStockOutDetails, d => d.updatedAt).map(toStockOutDetailsRow);

      const pushOps = [];
      if (invToPush.length > 0) pushOps.push(upsertSupabaseTable(TABLES.INVENTORY, invToPush));
      if (txToPush.length > 0) pushOps.push(upsertSupabaseTable(TABLES.TRANSACTIONS, txToPush));
      if (auditsToPush.length > 0) pushOps.push(upsertSupabaseTable(TABLES.STOCK_AUDITS, auditsToPush));
      if (somToPush.length > 0) pushOps.push(upsertSupabaseTable(TABLES.STOCK_OUT_MASTER, somToPush));
      if (sodToPush.length > 0) pushOps.push(upsertSupabaseTable(TABLES.STOCK_OUT_DETAILS, sodToPush));

      totalPushed = invToPush.length + txToPush.length + auditsToPush.length + somToPush.length + sodToPush.length;

      if (pushOps.length > 0) {
        await Promise.all(pushOps);
        setRealtimeSelfPauseUntil(Date.now() + SYNC_CONFIG.SELF_ECHO_PAUSE_MS);
      }
    }

    // 4. Settings Push / Pull
    const updatedSettings = { ...state.settings };
    if (pushSettings) {
      const storageLocationsToPush = state.settings.storageLocations || '';
      await upsertSupabaseTable(TABLES.APP_SETTINGS, [
        { key: 'lowStockThreshold', value: String(state.settings.lowStockThreshold || 5) },
        { key: 'expiryWarningDate', value: state.settings.expiryWarningDate || '' },
        { key: 'storageLocations', value: storageLocationsToPush }
      ], 'key');
      setRealtimeSelfPauseUntil(Date.now() + SYNC_CONFIG.SELF_ECHO_PAUSE_MS);
    } else {
      if (serverSettings.lowStockThreshold) {
        updatedSettings.lowStockThreshold = parseInt(serverSettings.lowStockThreshold, 10);
      }
      if (serverSettings.expiryWarningDate !== undefined) {
        updatedSettings.expiryWarningDate = serverSettings.expiryWarningDate;
      }
      if (serverSettings.storageLocations !== undefined) {
        updatedSettings.storageLocations = getResilientStorageLocations(
          serverSettings.storageLocations,
          forcePull,
          true,
          state.settings.storageLocations
        );
      }
    }

    // 5. Update State & LocalStorage
    setState({
      inventory: mergedInventory,
      transactions: mergedTransactions,
      stockAudits: mergedStockAudits,
      stockOutMaster: mergedStockOutMaster,
      stockOutDetails: mergedStockOutDetails,
      settings: updatedSettings,
      lastCloudSyncAt: syncStartedAt
    });

    setLastCloudSyncAt(syncStartedAt);
    saveLocalData();

    // Reset retry queue
    pendingSyncRetryAttempt = 0;
    pendingSyncRetryPushSettings = false;
    if (pendingSyncRetryTimer) {
      clearTimeout(pendingSyncRetryTimer);
      pendingSyncRetryTimer = null;
    }

    return { success: true, pushed: totalPushed, pulled: totalPulled };
  } catch (err) {
    console.error('Supabase sync execution error:', err);
    if (throwOnError) throw err;
    return { success: false, pushed: totalPushed, pulled: totalPulled, error: err };
  } finally {
    setCloudSyncInProgress(false);
    if (getCloudSyncQueued()) {
      setCloudSyncQueued(false);
      setTimeout(() => {
        if (hasCloudSyncConfig()) {
          syncWithSupabase({ forcePull: false });
        }
      }, 250);
    }
  }
}

/**
 * Alias for syncWithSupabase.
 */
export async function syncWithCloud(options = {}) {
  return syncWithSupabase(options);
}

/**
 * Schedules a background sync retry with exponential backoff delays [5s, 15s, 30s, 1m, 2m].
 * @param {string} [reason='']
 * @param {boolean} [pushSettings=false]
 */
export function schedulePendingSyncRetry(reason = '', pushSettings = false) {
  if (!hasCloudSyncConfig()) return;
  pendingSyncRetryPushSettings = pendingSyncRetryPushSettings || pushSettings;

  if (pendingSyncRetryTimer) {
    clearTimeout(pendingSyncRetryTimer);
    pendingSyncRetryTimer = null;
  }

  const delays = SYNC_CONFIG.RETRY_DELAYS_MS;
  const delay = delays[Math.min(pendingSyncRetryAttempt, delays.length - 1)];
  pendingSyncRetryAttempt += 1;

  pendingSyncRetryTimer = setTimeout(async () => {
    pendingSyncRetryTimer = null;
    try {
      const res = await syncWithSupabase({ pushSettings: pendingSyncRetryPushSettings, silent: true });
      if (res.success) {
        pendingSyncRetryAttempt = 0;
        pendingSyncRetryPushSettings = false;
      } else {
        schedulePendingSyncRetry(reason, pendingSyncRetryPushSettings);
      }
    } catch (err) {
      console.warn('Pending sync retry failed:', reason, err);
      schedulePendingSyncRetry(reason, pendingSyncRetryPushSettings);
    }
  }, delay);
}

/**
 * Cancels any active pending sync retry timer.
 */
export function cancelPendingSyncRetry() {
  if (pendingSyncRetryTimer) {
    clearTimeout(pendingSyncRetryTimer);
    pendingSyncRetryTimer = null;
  }
  pendingSyncRetryAttempt = 0;
  pendingSyncRetryPushSettings = false;
}

/**
 * Returns the current retry attempt count.
 * @returns {number}
 */
export function getPendingSyncRetryAttempt() {
  return pendingSyncRetryAttempt;
}

/**
 * Performs deferred background synchronization after a local mutation.
 * @param {string} [reason='local-change']
 * @param {boolean} [pushSettings=false]
 * @returns {Promise<boolean>}
 */
export async function syncAfterLocalChange(reason = 'local-change', pushSettings = false) {
  if (!hasCloudSyncConfig()) return false;
  try {
    const res = await syncWithSupabase({ pushSettings, silent: true, throwOnError: true });
    return res.success;
  } catch (err) {
    console.warn('Deferred sync after local change failed:', reason, err);
    schedulePendingSyncRetry(reason, pushSettings);
    return false;
  }
}

/**
 * Runs a background sync if not already in progress.
 * @param {string} [reason='background-sync']
 * @returns {Promise<boolean>}
 */
export async function runBackgroundSync(reason = 'background-sync') {
  if (backgroundSyncInProgress || !hasCloudSyncConfig()) return false;
  backgroundSyncInProgress = true;
  try {
    return await syncAfterLocalChange(reason);
  } finally {
    backgroundSyncInProgress = false;
  }
}

/**
 * Schedules a background sync only if the tab is visible and online.
 * @param {string} [reason='background-sync']
 */
export function scheduleVisibleBackgroundSync(reason = 'background-sync') {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  runBackgroundSync(reason);
}

/**
 * Attaches network online and visibilitychange listeners to resume syncs automatically.
 */
export function initSyncNetworkListeners() {
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      runBackgroundSync('network-online');
    });
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        scheduleVisibleBackgroundSync('visibility-visible');
      }
    });
  }
}
